/**
 * FornadaTable Component
 */

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Pencil, Trash2, Copy, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatCurrency } from "@/lib/utils";
import type { Fornada } from "@/types/fornada";

interface FornadaTableProps {
  fornadas: Fornada[];
  loading: boolean;
  onView: (fornada: Fornada) => void;
  onEdit: (fornada: Fornada) => void;
  onDelete: (fornada: Fornada) => void;
  onDuplicate: (id: string) => void;
}

export function FornadaTable({ fornadas, loading, onView, onEdit, onDelete, onDuplicate }: FornadaTableProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fornadas.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Nenhuma fornada encontrada</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fornada</TableHead>
            <TableHead>Data</TableHead>
            <TableHead>Responsável</TableHead>
            <TableHead>Destino</TableHead>
            <TableHead className="text-right">Rendimento</TableHead>
            <TableHead className="text-right">Custo Total</TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fornadas.map((fornada) => (
            <TableRow key={fornada.id_fornada}>
              <TableCell className="font-mono font-semibold">
                #{fornada.numero_fornada?.toString().padStart(6, '0') || 'N/A'}
              </TableCell>
              <TableCell>
                {format(new Date(fornada.data_fornada), "dd/MM/yyyy", { locale: ptBR })}
              </TableCell>
              <TableCell>{fornada.responsavel || "-"}</TableCell>
              <TableCell className="capitalize">{fornada.destino || "-"}</TableCell>
              <TableCell className="text-right">
                {fornada.rendimento_total ? `${fornada.rendimento_total} un` : "-"}
              </TableCell>
              <TableCell className="text-right font-semibold">
                {formatCurrency(fornada.custo_total)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="icon" onClick={() => onView(fornada)} title="Visualizar">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onEdit(fornada)} title="Editar">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDuplicate(fornada.id_fornada)} title="Duplicar">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(fornada)} title="Excluir">
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
